import PropTypes from 'prop-types';
import * as React from 'react';
import cx from 'classnames';
import { Column, Sort, sortDirection } from 'components/SmartTable';
import { SetSortAction } from 'reducers/tables';
import LayerTrigger from 'components/LayerTrigger';
import SmartTableHeaderSorts from 'components/SmartTableHeaderSorts';
import HovercardBody from 'components/HovercardBody';
import Hovercard from 'components/Hovercard';
import Glyph from 'components/Glyph';
import { setSort } from 'reducers/tables';
import { bindActionCreators } from 'redux';
import { connect, Dispatch } from 'react-redux';
import { findIndex } from 'lodash';
import { GlyphName } from '@remind/r101-glyphs';
import Text from 'components/Text';
import TooltipTrigger from 'components/TooltipTrigger';

import './styles/SmartTableHeaderColumn.scss';

type Props = {
  column: Column;
  sortKey?: string;
  setSort: (namespace: string, sortKey: string) => SetSortAction;
};

class SmartTableHeaderColumn extends React.Component<Props> {
  static contextTypes = {
    namespace: PropTypes.string.isRequired,
  };

  constructor(props: Props) {
    super(props);
    this.updateSortKeys(props.column);
  }

  componentWillReceiveProps(nextProps: Props) {
    if (nextProps.column !== this.props.column) {
      this.updateSortKeys(nextProps.column);
    }
  }

  columnSortKeys: Set<string> = new Set();

  updateSortKeys(column: Column) {
    if (!column.sorts) {
      this.columnSortKeys = new Set();
    } else {
      const sortKeys = column.sorts.map((sort: Sort) => sort.key);
      this.columnSortKeys = new Set(sortKeys);
    }
  }

  isPrimarySort(): boolean {
    if (this.props.sortKey) {
      return this.columnSortKeys.has(this.props.sortKey);
    }
    return false;
  }

  directionIcon(): GlyphName {
    if (!this.isPrimarySort()) {
      return 'chevron-down';
    }
    const direction = sortDirection(this.props?.sortKey);
    if (direction === 'asc') {
      return 'chevron-up';
    } else {
      return 'chevron-down';
    }
  }

  toggleSort = () => {
    const { column, sortKey } = this.props;
    const sorts = column.sorts;
    if (!sorts || sorts.length === 0) {
      return;
    }

    if (this.isPrimarySort()) {
      const currentIndex = findIndex(sorts, (sort: Sort) => sort.key === sortKey);
      const nextIndex = (currentIndex + 1) % sorts.length;
      this.props.setSort(this.context.namespace, sorts[nextIndex].key);
    } else {
      this.props.setSort(this.context.namespace, sorts[0].key);
    }
  };

  renderButton(column: Column, props?: Object) {
    return (
      <button {...props}>
        {column.name}
        {column.tooltip && (
          <TooltipTrigger placement="top" tooltip={column.tooltip}>
            <Glyph
              className="SmartTableHeaderColumn-tooltip"
              color="graphite"
              name="help"
              size="small"
            />
          </TooltipTrigger>
        )}
        {column.sorts && (
          <Glyph
            className={cx('SmartTableHeaderColumn-arrow', {
              'SmartTableHeaderColumn-arrow--primarySort': this.isPrimarySort(),
            })}
            name={this.directionIcon()}
            solo
          />
        )}
      </button>
    );
  }

  renderSorts(column: Column) {
    const { namespace } = this.context;
    const { setSort } = this.props;
    const sorts = column.sorts || [];

    if (sorts.length === 2) {
      return this.renderButton(column, { onClick: this.toggleSort });
    } else {
      return (
        <LayerTrigger
          ref="trigger"
          trigger="click"
          alignment="left"
          placement="bottom"
          layer={
            <Hovercard className="TableHeaderColumn-card">
              <HovercardBody>
                <SmartTableHeaderSorts
                  data-test="table-header-sorts"
                  namespace={namespace}
                  column={column}
                  setSort={setSort}
                  selectedSort={sorts[0].key}
                />
              </HovercardBody>
            </Hovercard>
          }>
          {this.renderButton(column)}
        </LayerTrigger>
      );
    }
  }

  render() {
    const { column } = this.props;
    const { sorts } = column;
    const child = sorts ? this.renderSorts(column) : column.name;

    return (
      <th className="SmartTableHeaderColumn">
        <Text wrap={false}>{child}</Text>
      </th>
    );
  }
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
  return bindActionCreators({ setSort }, dispatch);
}
export { SmartTableHeaderColumn };
export default connect(null, mapDispatchToProps)(SmartTableHeaderColumn);
